import type { Resource, ResourceKey } from 'i18next';

// 기본 번역 파일들
import translationEN from './en/translation.json';
import translationKTDSEN from './en/translation_ktds.json';
import translationKO from './ko/translation.json';
import translationKTDSKO from './ko/translation_ktds.json';

// 번역 객체 타입 정의 (실제 번역 내용)
type TranslationObject = Record<
  string,
  | string
  | {
      [key: string]: string;
    }
>;

/**
 * 기본 번역 리소스를 로드하는 함수
 */
export const loadTranslationResources = (): Resource => {
  return {
    en: { translation: translationEN },
    ko: { translation: translationKO },
  };
};

// 번역 객체 타입 검증 함수
const isValidTranslation = (obj: ResourceKey): obj is Record<string, TranslationObject> => {
  return obj !== null && typeof obj === 'object' && !Array.isArray(obj);
};

/**
 * Deep merge 함수 - 중첩된 객체도 병합
 */
const deepMerge = (target: any, source: any): any => {
  const output = { ...target };

  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach(key => {
      if (isObject(source[key])) {
        if (!(key in target)) {
          Object.assign(output, { [key]: source[key] });
        } else {
          output[key] = deepMerge(target[key], source[key]);
        }
      } else {
        Object.assign(output, { [key]: source[key] });
      }
    });
  }

  return output;
};

const isObject = (item: any): boolean => {
  return item && typeof item === 'object' && !Array.isArray(item);
};

/**
 * 회사별 번역 생성 함수 (지연 로딩 패턴)
 * 1. 필요한 회사의 번역만 로드
 * 2. 앱 시작 시 모든 번역을 메모리에 올리지 않음
 * 3. 사용하지 않는 번역 데이터 처리 시간 절약
 */
const getCompanyTranslations = (companyId: CompanyType): Resource => {
  const overrideMap: Record<CompanyType, () => Resource> = {
    MEGAZONE: () => ({
      ko: { translation: translationKO },
      en: { translation: translationEN },
    }),
    ZENITH: () => ({
      ko: { translation: translationKO },
      en: { translation: translationEN },
    }),
    KT_DS: () => ({
      ko: {
        translation: deepMerge(translationKO, translationKTDSKO), // Deep merge로 중첩 객체도 병합
      },
      en: {
        translation: deepMerge(translationEN, translationKTDSEN), // Deep merge로 중첩 객체도 병합
      },
    }),
  };

  const createTranslation = overrideMap[companyId];
  if (!createTranslation) {
    // 기본값 반환
    return {
      ko: { translation: translationKO },
      en: { translation: translationEN },
    };
  }

  return createTranslation();
};

/**
 * 회사별 번역 오버라이드 함수
 */
export const createCompanyTranslations = (
  baseTranslations: Resource,
  companyId: CompanyType,
  enableCustomTranslation: string,
): {
  resources: Resource;
  language: string;
} => {
  const detectedLanguage = detectLanguage();

  // 커스텀 번역을 사용하지 않는 경우 기본 번역 반환
  if (enableCustomTranslation !== 'true') {
    return { resources: baseTranslations, language: detectedLanguage };
  }

  // 지연 로딩으로 필요한 시점에만 번역 생성
  const companyTranslations = getCompanyTranslations(companyId);

  // 추가 타입 안전성 검증
  if (isValidTranslation(companyTranslations[detectedLanguage]?.translation)) {
    return { resources: companyTranslations, language: detectedLanguage };
  }

  // 기본 번역 반환
  return { resources: baseTranslations, language: detectedLanguage };
};

/**
 * 언어 감지 및 폴백 처리 (기본 ko, en 지원)
 * 추후 회사별로 언어를 지원하는 경우 수정할 수 있게 확장성 있게 설계
 */
export const detectLanguage = (): string => {
  // 브라우저 언어 감지
  const browserLang = navigator.language.split('-')[0];

  // 지원하는 언어인지 확인 (ko, en만 지원)
  const supportedLanguages = ['ko', 'en'];
  if (supportedLanguages.includes(browserLang)) {
    return browserLang;
  }

  // 기본값은 한국어
  return 'ko';
};
